import { FC, useCallback, useMemo, useState } from 'react'
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  MouseSensor,
  pointerWithin,
  rectIntersection,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { useFormContext } from 'react-hook-form'
import { v4 as uuidv4 } from 'uuid'
import {
  ContextMenuAction,
  FormElement,
  FormElementType,
  MobileForm,
  MobileListItem,
  Variable,
} from '@mobileTypes'
import {
  FORM_ELEMENT_ICONS,
  FORM_ELEMENT_TYPES,
  getFormElementIcon,
  getFormElementSummary,
} from '@mobileConstants'
import { Box, Chip, Divider, Paper, Typography } from '@mui/material'
import { DragIndicator } from '@mui/icons-material'

import { getDefaultFormFieldData } from '@mobileHelpers'
import { useMobileGlobalVariables } from '@mobileHooks'

import { AvailableElementsPanel } from './AvailableElementsPanel'
import { ConfiguredFormPanel } from './ConfiguredFormPanel'
import { ElementConfig, ElementConfigDialog } from './ElementConfigDialog'
import { EmbeddedListConfigDialog } from './EmbeddedListConfigDialog'
import { ListItemConfigDialog } from './ListItemConfigDialog'

/** Applies BUTTON/SWIPE config. Command/actions removed — handled via Event Flows. SWIPE keeps backgroundColor only. */
function applyButtonOrSwipeConfig(element: FormElement, config: ElementConfig): void {
  element.command = undefined
  element.actions = undefined
  element.chooseActionJs = undefined
  if (config.type === 'SWIPE') {
    element.backgroundColor = config.backgroundColor
  }
  if (config.type === 'BUTTON') {
    element.buttonColor = config.buttonColor
  }
}

type Destination = { droppableId: string; index: number }

type ActiveDescriptor =
  | { kind: 'palette'; elementType: FormElementType }
  | { kind: 'item'; element: FormElement }

export const FormElementsConfigTab: FC = () => {
  const { watch, setValue } = useFormContext<MobileForm>()

  const elements = watch('elements') || []
  const bottomElements = watch('bottomElements') || []
  const variables = watch('variables') || []
  const eventFlows = watch('eventFlows') || []
  const allFormElements = [...elements, ...bottomElements]

  const { variables: globalVariables, createGlobalVariable } = useMobileGlobalVariables()

  const handleCreateVariable = useCallback(
    (
      variableInternalId: string,
      isGlobal: boolean = false,
      valueType: string = 'string',
      variableType: string = 'single'
    ) => {
      if (!variableInternalId || !variableInternalId.trim()) {
        return
      }

      const trimmed = variableInternalId.trim()

      const existingLocalVariable = variables.find((v: Variable) => v.internalId === trimmed)
      if (existingLocalVariable) {
        return
      }

      const existingGlobalVariable = globalVariables.find((v: Variable) => v.internalId === trimmed)
      if (existingGlobalVariable) {
        return
      }

      const newVariable: Omit<Variable, 'id'> = {
        internalId: trimmed,
        variableType: variableType === 'multiple' ? 'multiple' : 'single',
        valueType: valueType,
        defaultValue: undefined,
      }

      if (isGlobal) {
        createGlobalVariable(newVariable)
      } else {
        const localVariable: Variable = {
          ...newVariable,
          id: uuidv4(),
        }
        const updatedVariables = [...variables, localVariable]
        setValue('variables', updatedVariables, { shouldDirty: true })
      }
    },
    [variables, globalVariables, setValue, createGlobalVariable]
  )

  const [configDialogState, setConfigDialogState] = useState<{
    open: boolean
    mode: 'create' | 'edit'
    elementType?: FormElementType
    destination?: Destination
    editIndex?: number
    isBottom?: boolean
    accordionParent?: { index: number; isBottom: boolean }
    initialData?: Partial<FormElement>
  }>({
    open: false,
    mode: 'create',
  })

  const [embeddedListDialogState, setEmbeddedListDialogState] = useState<{
    open: boolean
    mode: 'create' | 'edit'
    destination?: Destination
    editIndex?: number
    isBottom?: boolean
    accordionParent?: { index: number; isBottom: boolean }
    initialData?: Partial<FormElement>
  }>({
    open: false,
    mode: 'create',
  })

  const [listItemDialogState, setListItemDialogState] = useState<{
    open: boolean
    mode: 'create' | 'edit'
    destination?: Destination
    editIndex?: number
    isBottom?: boolean
    accordionParent?: { index: number; isBottom: boolean }
    initialData?: Partial<FormElement>
  }>({
    open: false,
    mode: 'create',
  })

  const [activeId, setActiveId] = useState<string | null>(null)
  const [activeDescriptor, setActiveDescriptor] = useState<ActiveDescriptor | null>(null)

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const parseAccordionDroppableId = useCallback((droppableId: string) => {
    const match = droppableId.match(/^ACCORDION:(MAIN|BOTTOM):(\d+)$/)
    if (!match) return null

    return {
      isBottom: match[1] === 'BOTTOM',
      index: Number(match[2]),
    }
  }, [])

  const getListPathByDroppableId = useCallback(
    (droppableId: string): 'elements' | 'bottomElements' | null => {
      if (droppableId === 'MAIN_ELEMENTS') return 'elements'
      if (droppableId === 'BOTTOM_ELEMENTS') return 'bottomElements'
      return null
    },
    []
  )

  const insertElementAtDestination = useCallback(
    (destination: Destination, element: FormElement) => {
      const accordionTarget = parseAccordionDroppableId(destination.droppableId)
      if (accordionTarget) {
        const listPath = accordionTarget.isBottom ? 'bottomElements' : 'elements'
        const targetList = accordionTarget.isBottom ? bottomElements : elements
        const updatedList = [...targetList]
        const accordion = { ...updatedList[accordionTarget.index] }
        const children = [...(accordion.accordionChildren || [])]

        children.splice(destination.index, 0, element)
        accordion.accordionChildren = children.map((child, idx) => ({ ...child, order: idx }))
        updatedList[accordionTarget.index] = accordion
        setValue(
          listPath,
          updatedList.map((el, idx) => ({ ...el, order: idx })),
          { shouldDirty: true }
        )
        return
      }

      const listPath = getListPathByDroppableId(destination.droppableId)
      if (!listPath) return

      const targetList = listPath === 'bottomElements' ? bottomElements : elements
      const updatedList = [...targetList]
      updatedList.splice(destination.index, 0, element)
      setValue(
        listPath,
        updatedList.map((el, idx) => ({ ...el, order: idx })),
        { shouldDirty: true }
      )
    },
    [bottomElements, elements, getListPathByDroppableId, parseAccordionDroppableId, setValue]
  )

  const moveExistingElement = useCallback(
    (source: Destination, destination: Destination) => {
      const sourceAccordion = parseAccordionDroppableId(source.droppableId)
      const destinationAccordion = parseAccordionDroppableId(destination.droppableId)

      // Prevent dropping accordion into itself
      if (
        !sourceAccordion &&
        destinationAccordion &&
        source.index === destinationAccordion.index &&
        ((source.droppableId === 'MAIN_ELEMENTS' && !destinationAccordion.isBottom) ||
          (source.droppableId === 'BOTTOM_ELEMENTS' && destinationAccordion.isBottom))
      ) {
        return
      }

      const nextElements = [...elements]
      const nextBottomElements = [...bottomElements]

      const getTopList = (isBottom: boolean) => (isBottom ? nextBottomElements : nextElements)
      const getTopListPath = (isBottom: boolean) => (isBottom ? 'bottomElements' : 'elements')

      const removeFromSource = (): FormElement | undefined => {
        if (sourceAccordion) {
          const sourceList = getTopList(sourceAccordion.isBottom)
          const accordion = { ...sourceList[sourceAccordion.index] }
          const children = [...(accordion.accordionChildren || [])]
          const [removed] = children.splice(source.index, 1)
          accordion.accordionChildren = children.map((child, idx) => ({ ...child, order: idx }))
          sourceList[sourceAccordion.index] = accordion
          return removed
        }

        const sourcePath = getListPathByDroppableId(source.droppableId)
        if (!sourcePath) return undefined

        const sourceList = sourcePath === 'bottomElements' ? nextBottomElements : nextElements
        const [removed] = sourceList.splice(source.index, 1)
        return removed
      }

      const insertToDestination = (element: FormElement) => {
        if (destinationAccordion) {
          const sameTopListSource =
            !sourceAccordion &&
            ((source.droppableId === 'MAIN_ELEMENTS' && !destinationAccordion.isBottom) ||
              (source.droppableId === 'BOTTOM_ELEMENTS' && destinationAccordion.isBottom))
          const destinationAccordionIndex =
            sameTopListSource && source.index < destinationAccordion.index
              ? destinationAccordion.index - 1
              : destinationAccordion.index
          const destinationList = getTopList(destinationAccordion.isBottom)
          const accordion = { ...destinationList[destinationAccordionIndex] }
          const children = [...(accordion.accordionChildren || [])]
          children.splice(destination.index, 0, element)
          accordion.accordionChildren = children.map((child, idx) => ({ ...child, order: idx }))
          destinationList[destinationAccordionIndex] = accordion
          return
        }

        const destinationPath = getListPathByDroppableId(destination.droppableId)
        if (!destinationPath) return

        const destinationList =
          destinationPath === 'bottomElements' ? nextBottomElements : nextElements
        destinationList.splice(destination.index, 0, element)
      }

      const removed = removeFromSource()
      if (!removed) return
      insertToDestination(removed)

      const touchedMain =
        (!sourceAccordion && source.droppableId === 'MAIN_ELEMENTS') ||
        (!destinationAccordion && destination.droppableId === 'MAIN_ELEMENTS') ||
        sourceAccordion?.isBottom === false ||
        destinationAccordion?.isBottom === false
      const touchedBottom =
        (!sourceAccordion && source.droppableId === 'BOTTOM_ELEMENTS') ||
        (!destinationAccordion && destination.droppableId === 'BOTTOM_ELEMENTS') ||
        sourceAccordion?.isBottom === true ||
        destinationAccordion?.isBottom === true

      if (touchedMain) {
        setValue(
          getTopListPath(false),
          nextElements.map((el, idx) => ({ ...el, order: idx })),
          { shouldDirty: true }
        )
      }
      if (touchedBottom) {
        setValue(
          getTopListPath(true),
          nextBottomElements.map((el, idx) => ({ ...el, order: idx })),
          { shouldDirty: true }
        )
      }
    },
    [bottomElements, elements, getListPathByDroppableId, parseAccordionDroppableId, setValue]
  )

  const getElementByItemId = useCallback(
    (itemId: string): FormElement | null => {
      const accordionMatch = itemId.match(/^ACCORDION:(MAIN|BOTTOM):(\d+):(\d+)$/)
      if (accordionMatch) {
        const isBottom = accordionMatch[1] === 'BOTTOM'
        const accordionIndex = Number(accordionMatch[2])
        const childIndex = Number(accordionMatch[3])
        const list = isBottom ? bottomElements : elements
        return list[accordionIndex]?.accordionChildren?.[childIndex] || null
      }

      const topMatch = itemId.match(/^(MAIN_ELEMENTS|BOTTOM_ELEMENTS):(\d+)$/)
      if (topMatch) {
        const list = topMatch[1] === 'BOTTOM_ELEMENTS' ? bottomElements : elements
        return list[Number(topMatch[2])] || null
      }

      return null
    },
    [elements, bottomElements]
  )

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const { active } = event
      const id = active.id as string
      setActiveId(id)

      const data = active.data.current
      if (data?.type === 'palette') {
        setActiveDescriptor({
          kind: 'palette',
          elementType: data.elementType as FormElementType,
        })
      } else if (data?.type === 'item') {
        const element = getElementByItemId(id)
        if (element) {
          setActiveDescriptor({ kind: 'item', element })
        } else {
          setActiveDescriptor(null)
        }
      } else {
        setActiveDescriptor(null)
      }
    },
    [getElementByItemId]
  )

  const handleDragCancel = useCallback(() => {
    setActiveId(null)
    setActiveDescriptor(null)
  }, [])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      setActiveId(null)
      setActiveDescriptor(null)

      if (!over) return

      const activeData = active.data.current
      const overData = over.data.current
      if (!activeData || !overData) return

      // Resolve destination from `over` element
      let destination: Destination | null = null
      if (overData.type === 'item') {
        destination = {
          droppableId: overData.containerId as string,
          index: overData.index as number,
        }
      } else if (overData.type === 'container') {
        destination = {
          droppableId: overData.containerId as string,
          index: (overData.endIndex as number) ?? 0,
        }
      } else {
        return
      }

      // Palette drop -> create flow
      if (activeData.type === 'palette') {
        const elementType = activeData.elementType as FormElementType

        if (elementType === 'DIVIDER') {
          const newElement: FormElement = {
            type: 'DIVIDER',
            order: destination.index,
            size: 12,
            title: '',
            textAlign: 'LEFT',
            labelPosition: 'TOP',
            editable: false,
            titleHidden: true,
          }
          insertElementAtDestination(destination, newElement)
          return
        }

        if (elementType === 'EMBEDDED_LIST') {
          setEmbeddedListDialogState({
            open: true,
            mode: 'create',
            destination,
          })
          return
        }

        if (elementType === 'LIST_ITEM') {
          setListItemDialogState({
            open: true,
            mode: 'create',
            destination,
          })
          return
        }

        setConfigDialogState({
          open: true,
          mode: 'create',
          elementType,
          destination,
        })
        return
      }

      // Existing item move
      if (activeData.type === 'item') {
        const source: Destination = {
          droppableId: activeData.containerId as string,
          index: activeData.index as number,
        }

        // No-op if dropped on itself
        if (
          source.droppableId === destination.droppableId &&
          source.index === destination.index
        ) {
          return
        }

        moveExistingElement(source, destination)
      }
    },
    [insertElementAtDestination, moveExistingElement]
  )

  const handleSaveElementConfig = useCallback(
    (config: ElementConfig) => {
      if (configDialogState.mode === 'create') {
        const newElement: FormElement = {
          type: config.type,
          internalId: config.internalId || undefined,
          order: configDialogState.destination!.index,
          size: 12,
          title:
            config.type === 'ACCORDION' ? config.accordionTitle || '' : config.title || '',
          titleJs: config.titleJs || undefined,
          textAlign: config.textAlign || 'LEFT',
          labelPosition: 'TOP',
          editable: config.readOnly ? false : true,
          titleHidden: false,
          visibleIfJs: config.visibleIfJs,
        }

        if (config.type === 'FIELD') {
          newElement.data = getDefaultFormFieldData({
            code: config.fieldCode || '',
            title: config.title || '',
            value: config.bindType === 'js' ? config.valueJs || '' : config.fieldCode || '',
            valuePath: config.bindType === 'js' ? config.valuePath || undefined : undefined,
            objectCode: config.variableObjectCode || config.objectCode || '',
            variableName: config.variableName || '',
            variableObjectCode: config.variableObjectCode || '',
            valueType: config.valueType || 'string',
            bindType: config.bindType || 'field',
            pathArray: config.pathArray || null,
            formatDate: config.formatDate || null,
            type: config.fieldType || 'single',
            userRequired: config.required || false,
            editField: config.readOnly ? false : true,
            isMultiline: config.multiline || false,
            placeholderValue: config.placeholder || '',
          })
        } else if (config.type === 'BUTTON' || config.type === 'SWIPE') {
          applyButtonOrSwipeConfig(newElement, config)
        } else if (config.type === 'EMBEDDED_LIST') {
          newElement.embeddedListItemId = config.embeddedListItemId
          newElement.embeddedListConfig = config.embeddedListConfig
          newElement.selectedIdsVariable = config.selectedIdsVariable
          newElement.showCheckboxes =
            config.showCheckboxes !== undefined ? config.showCheckboxes : true
          newElement.itemIdField = config.itemIdField || '_id'
          newElement.contextMenuActions = config.contextMenuActions
          newElement.listHeader = config.listHeader
          newElement.listSubheader = config.listSubheader
          newElement.listSubheaderJs = config.listSubheaderJs
          newElement.selectAllEnabled = config.selectAllEnabled
          newElement.selectAllText = config.selectAllText
          newElement.variableBindings = config.variableBindings
          newElement.readOnlyIfJs = config.readOnlyIfJs
        } else if (config.type === 'ACCORDION') {
          newElement.style = config.accordionStyle
          newElement.accordionChildren = config.accordionChildren || []
        } else if (config.type === 'FILE') {
          newElement.fileTypes = config.fileTypes || []
          newElement.fileVariableName = config.fileVariableName || ''
        }

        insertElementAtDestination(configDialogState.destination!, newElement)
      } else {
        const isAccordionChild = Boolean(configDialogState.accordionParent)
        const isBottom = configDialogState.accordionParent?.isBottom ?? configDialogState.isBottom!
        const targetList = isBottom ? bottomElements : elements
        const updatedList = [...targetList]
        const accordion = configDialogState.accordionParent
          ? { ...updatedList[configDialogState.accordionParent.index] }
          : undefined
        const accordionChildren = accordion ? [...(accordion.accordionChildren || [])] : undefined
        const element = {
          ...(isAccordionChild
            ? accordionChildren![configDialogState.editIndex!]
            : updatedList[configDialogState.editIndex!]),
        }

        element.internalId = config.internalId ?? element.internalId
        element.title =
          config.type === 'ACCORDION'
            ? config.accordionTitle ?? element.title
            : config.title || ''
        element.titleJs = config.titleJs || undefined
        element.textAlign = config.textAlign || 'LEFT'
        element.editable = config.readOnly ? false : true
        element.visibleIfJs = config.visibleIfJs

        if (element.type === 'FIELD' && element.data) {
          element.data = {
            ...element.data,
            code: config.fieldCode || '',
            title: config.title || '',
            value: config.bindType === 'js' ? config.valueJs || '' : config.fieldCode || '',
            valuePath: config.bindType === 'js' ? config.valuePath || undefined : undefined,
            objectCode: config.variableObjectCode || config.objectCode || '',
            variableName: config.variableName || '',
            variableObjectCode: config.variableObjectCode || '',
            bindType: config.bindType || 'field',
            valueType: config.valueType || 'string',
            formatDate: config.formatDate || null,
            type: config.fieldType || 'single',
            userRequired: config.required || false,
            isMultiline: config.multiline || false,
            placeholderValue: config.placeholder || '',
            editField: config.readOnly ? false : true,
            pathArray: config.pathArray || null,
          }
        } else if (element.type === 'BUTTON' || element.type === 'SWIPE') {
          applyButtonOrSwipeConfig(element, config)
        } else if (element.type === 'EMBEDDED_LIST') {
          element.embeddedListItemId = config.embeddedListItemId
          element.embeddedListConfig = config.embeddedListConfig
          element.selectedIdsVariable = config.selectedIdsVariable
          element.showCheckboxes =
            config.showCheckboxes !== undefined ? config.showCheckboxes : true
          element.itemIdField = config.itemIdField || '_id'
          element.contextMenuActions = config.contextMenuActions
          element.listHeader = config.listHeader
          element.listSubheader = config.listSubheader
          element.listSubheaderJs = config.listSubheaderJs
          element.selectAllEnabled = config.selectAllEnabled
          element.selectAllText = config.selectAllText
          element.variableBindings = config.variableBindings
          element.readOnlyIfJs = config.readOnlyIfJs
        } else if (element.type === 'ACCORDION') {
          element.style = config.accordionStyle
          element.accordionChildren = config.accordionChildren || []
        } else if (element.type === 'FILE') {
          element.fileTypes = config.fileTypes || []
          element.fileVariableName = config.fileVariableName || ''
        }

        if (isAccordionChild) {
          accordionChildren![configDialogState.editIndex!] = element
          accordion!.accordionChildren = accordionChildren!.map((el, idx) => ({
            ...el,
            order: idx,
          }))
          updatedList[configDialogState.accordionParent!.index] = accordion!
        } else {
          updatedList[configDialogState.editIndex!] = element
        }
        const finalList = updatedList.map((el, idx) => ({ ...el, order: idx }))
        setValue(isBottom ? 'bottomElements' : 'elements', finalList, { shouldDirty: true })
      }

      setConfigDialogState({ open: false, mode: 'create' })
    },
    [configDialogState, elements, bottomElements, setValue, insertElementAtDestination]
  )

  const handleSaveEmbeddedListConfig = useCallback(
    (config: {
      internalId?: string
      embeddedListItemId?: string
      embeddedListConfig?: Partial<MobileListItem>
      selectedIdsVariable?: string
      showCheckboxes?: boolean
      itemIdField?: string
      contextMenuActions?: ContextMenuAction[]
      listHeader?: string
      listSubheader?: string
      listSubheaderJs?: string
      selectAllEnabled?: boolean
      selectAllText?: string
      variableBindings?: Record<string, string>
      readOnlyIfJs?: string
    }) => {
      if (embeddedListDialogState.mode === 'create') {
        const newElement: FormElement = {
          type: 'EMBEDDED_LIST',
          order: embeddedListDialogState.destination!.index,
          size: 12,
          title: '',
          textAlign: 'LEFT',
          labelPosition: 'TOP',
          editable: false,
          titleHidden: true,
          internalId: config.internalId,
          embeddedListItemId: config.embeddedListItemId,
          embeddedListConfig: config.embeddedListConfig,
          selectedIdsVariable: config.selectedIdsVariable,
          showCheckboxes:
            config.showCheckboxes !== undefined ? config.showCheckboxes : true,
          itemIdField: config.itemIdField || '_id',
          contextMenuActions: config.contextMenuActions,
          variableBindings: config.variableBindings,
          listHeader: config.listHeader,
          listSubheader: config.listSubheader,
          listSubheaderJs: config.listSubheaderJs,
          selectAllEnabled: config.selectAllEnabled,
          selectAllText: config.selectAllText,
          readOnlyIfJs: config.readOnlyIfJs,
        }

        insertElementAtDestination(embeddedListDialogState.destination!, newElement)
      } else {
        const isAccordionChild = Boolean(embeddedListDialogState.accordionParent)
        const isBottom =
          embeddedListDialogState.accordionParent?.isBottom ?? embeddedListDialogState.isBottom!
        const targetList = isBottom ? bottomElements : elements
        const updatedList = [...targetList]
        const accordion = embeddedListDialogState.accordionParent
          ? { ...updatedList[embeddedListDialogState.accordionParent.index] }
          : undefined
        const accordionChildren = accordion ? [...(accordion.accordionChildren || [])] : undefined
        const element = {
          ...(isAccordionChild
            ? accordionChildren![embeddedListDialogState.editIndex!]
            : updatedList[embeddedListDialogState.editIndex!]),
        }

        element.internalId = config.internalId
        element.embeddedListItemId = config.embeddedListItemId
        element.embeddedListConfig = config.embeddedListConfig
        element.selectedIdsVariable = config.selectedIdsVariable
        element.showCheckboxes =
          config.showCheckboxes !== undefined ? config.showCheckboxes : true
        element.itemIdField = config.itemIdField || '_id'
        element.contextMenuActions = config.contextMenuActions
        element.listHeader = config.listHeader
        element.listSubheader = config.listSubheader
        element.listSubheaderJs = config.listSubheaderJs
        element.selectAllEnabled = config.selectAllEnabled
        element.selectAllText = config.selectAllText
        element.variableBindings = config.variableBindings
        element.readOnlyIfJs = config.readOnlyIfJs

        if (isAccordionChild) {
          accordionChildren![embeddedListDialogState.editIndex!] = element
          accordion!.accordionChildren = accordionChildren!.map((el, idx) => ({
            ...el,
            order: idx,
          }))
          updatedList[embeddedListDialogState.accordionParent!.index] = accordion!
        } else {
          updatedList[embeddedListDialogState.editIndex!] = element
        }
        const finalList = updatedList.map((el, idx) => ({ ...el, order: idx }))
        setValue(isBottom ? 'bottomElements' : 'elements', finalList, { shouldDirty: true })
      }

      setEmbeddedListDialogState({ open: false, mode: 'create' })
    },
    [embeddedListDialogState, elements, bottomElements, setValue, insertElementAtDestination]
  )

  const handleSaveListItemConfig = useCallback(
    (config: { internalId?: string; listItemConfigId?: string; listItemDataVariable?: string }) => {
      if (listItemDialogState.mode === 'create') {
        const newElement: FormElement = {
          type: 'LIST_ITEM',
          order: listItemDialogState.destination!.index,
          size: 12,
          title: '',
          textAlign: 'LEFT',
          labelPosition: 'TOP',
          editable: false,
          titleHidden: true,
          internalId: config.internalId,
          listItemConfigId: config.listItemConfigId,
          listItemDataVariable: config.listItemDataVariable,
        }

        insertElementAtDestination(listItemDialogState.destination!, newElement)
      } else {
        const isAccordionChild = Boolean(listItemDialogState.accordionParent)
        const isBottom =
          listItemDialogState.accordionParent?.isBottom ?? listItemDialogState.isBottom!
        const targetList = isBottom ? bottomElements : elements
        const updatedList = [...targetList]
        const accordion = listItemDialogState.accordionParent
          ? { ...updatedList[listItemDialogState.accordionParent.index] }
          : undefined
        const accordionChildren = accordion ? [...(accordion.accordionChildren || [])] : undefined
        const element = {
          ...(isAccordionChild
            ? accordionChildren![listItemDialogState.editIndex!]
            : updatedList[listItemDialogState.editIndex!]),
        }

        element.internalId = config.internalId
        element.listItemConfigId = config.listItemConfigId
        element.listItemDataVariable = config.listItemDataVariable

        if (isAccordionChild) {
          accordionChildren![listItemDialogState.editIndex!] = element
          accordion!.accordionChildren = accordionChildren!.map((el, idx) => ({
            ...el,
            order: idx,
          }))
          updatedList[listItemDialogState.accordionParent!.index] = accordion!
        } else {
          updatedList[listItemDialogState.editIndex!] = element
        }
        const finalList = updatedList.map((el, idx) => ({ ...el, order: idx }))
        setValue(isBottom ? 'bottomElements' : 'elements', finalList, { shouldDirty: true })
      }

      setListItemDialogState({ open: false, mode: 'create' })
    },
    [listItemDialogState, elements, bottomElements, setValue, insertElementAtDestination]
  )

  const handleEditElement = useCallback(
    (
      index: number,
      isBottom: boolean,
      accordionParent?: { index: number; isBottom: boolean }
    ) => {
      const targetList = isBottom ? bottomElements : elements
      const element = accordionParent
        ? targetList[accordionParent.index]?.accordionChildren?.[index]
        : targetList[index]

      if (!element) return

      if (element.type === 'EMBEDDED_LIST') {
        setEmbeddedListDialogState({
          open: true,
          mode: 'edit',
          editIndex: index,
          isBottom,
          accordionParent,
          initialData: element,
        })
        return
      }

      if (element.type === 'LIST_ITEM') {
        setListItemDialogState({
          open: true,
          mode: 'edit',
          editIndex: index,
          isBottom,
          accordionParent,
          initialData: element,
        })
        return
      }

      setConfigDialogState({
        open: true,
        mode: 'edit',
        editIndex: index,
        isBottom,
        accordionParent,
        initialData: element,
      })
    },
    [elements, bottomElements]
  )

  const handleDeleteElement = useCallback(
    (
      index: number,
      isBottom: boolean,
      accordionParent?: { index: number; isBottom: boolean }
    ) => {
      const targetList = isBottom ? bottomElements : elements
      const updatedList = [...targetList]

      if (accordionParent) {
        const accordion = { ...updatedList[accordionParent.index] }
        accordion.accordionChildren = (accordion.accordionChildren || [])
          .filter((_, idx) => idx !== index)
          .map((el, idx) => ({ ...el, order: idx }))
        updatedList[accordionParent.index] = accordion
      } else {
        updatedList.splice(index, 1)
      }

      const finalList = updatedList.map((el, idx) => ({ ...el, order: idx }))
      setValue(isBottom ? 'bottomElements' : 'elements', finalList, { shouldDirty: true })
    },
    [elements, bottomElements, setValue]
  )

  // Custom collision detection: prefer pointer-within (for nested droppables like accordion content),
  // fallback to rectangle intersection so empty containers/list ends still get hits.
  const collisionDetection = useCallback((args: Parameters<typeof rectIntersection>[0]) => {
    const pointerCollisions = pointerWithin(args)
    if (pointerCollisions.length > 0) {
      return pointerCollisions
    }
    return rectIntersection(args)
  }, [])

  const dragOverlayContent = useMemo(() => {
    if (!activeDescriptor) return null

    if (activeDescriptor.kind === 'palette') {
      const meta = FORM_ELEMENT_TYPES.find(t => t.value === activeDescriptor.elementType)

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            py: 1.5,
            px: 2,
            gap: 1.5,
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'primary.main',
            borderRadius: 1,
            boxShadow: 6,
            opacity: 0.95,
            minWidth: 220,
          }}
        >
          <DragIndicator sx={{ color: 'text.secondary', fontSize: 20 }} />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'primary.main',
              mr: 0.5,
            }}
          >
            {FORM_ELEMENT_ICONS[activeDescriptor.elementType]}
          </Box>
          <Typography variant='body2' fontWeight='500'>
            {meta?.label || activeDescriptor.elementType}
          </Typography>
        </Box>
      )
    }

    const element = activeDescriptor.element

    return (
      <Paper
        variant='outlined'
        sx={{
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          backgroundColor: 'background.paper',
          borderColor: 'primary.main',
          boxShadow: 6,
          minWidth: 240,
          opacity: 0.95,
        }}
      >
        <DragIndicator fontSize='small' sx={{ color: 'text.secondary' }} />
        <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
          {getFormElementIcon(element.type, { fontSize: 'small' })}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant='body2' fontWeight='500' noWrap>
              {getFormElementSummary(element)}
            </Typography>
            <Chip
              label={element.type}
              size='small'
              sx={{ height: 18, fontSize: '0.65rem' }}
            />
          </Box>
        </Box>
      </Paper>
    )
  }, [activeDescriptor])

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <Box sx={{ display: 'flex', height: '600px', gap: 2 }}>
          <AvailableElementsPanel />
          <Divider orientation='vertical' flexItem />
          <ConfiguredFormPanel
            elements={elements}
            bottomElements={bottomElements}
            onEditElement={handleEditElement}
            onDeleteElement={handleDeleteElement}
            activeId={activeId}
          />
        </Box>
        <DragOverlay dropAnimation={null}>{dragOverlayContent}</DragOverlay>
      </DndContext>

      {configDialogState.open && (
        <ElementConfigDialog
          open={configDialogState.open}
          mode={configDialogState.mode}
          elementType={configDialogState.elementType}
          initialData={configDialogState.initialData}
          variables={variables}
          onClose={() => setConfigDialogState({ open: false, mode: 'create' })}
          onSave={handleSaveElementConfig}
          onCreateVariable={handleCreateVariable}
          eventFlows={eventFlows}
          onEventFlowsChange={flows => setValue('eventFlows', flows, { shouldDirty: true })}
          elementRef={
            configDialogState.mode === 'edit' && configDialogState.initialData?.internalId
              ? configDialogState.initialData.type === 'BUTTON'
                ? { type: 'button', elementInternalId: configDialogState.initialData.internalId }
                : configDialogState.initialData.type === 'SWIPE'
                  ? { type: 'swipe', elementInternalId: configDialogState.initialData.internalId }
                  : configDialogState.initialData.type === 'FIELD'
                    ? { type: 'field', fieldInternalId: configDialogState.initialData.internalId }
                    : configDialogState.initialData.type === 'EMBEDDED_LIST'
                      ? { type: 'embedded_list', elementId: configDialogState.initialData.internalId }
                      : undefined
              : undefined
          }
          formElements={allFormElements}
        />
      )}

      {embeddedListDialogState.open && (
        <EmbeddedListConfigDialog
          open={embeddedListDialogState.open}
          initialData={embeddedListDialogState.initialData}
          variables={variables}
          onClose={() => setEmbeddedListDialogState({ open: false, mode: 'create' })}
          onSave={handleSaveEmbeddedListConfig}
          onCreateVariable={handleCreateVariable}
        />
      )}

      {listItemDialogState.open && (
        <ListItemConfigDialog
          open={listItemDialogState.open}
          initialData={listItemDialogState.initialData}
          variables={variables}
          onClose={() => setListItemDialogState({ open: false, mode: 'create' })}
          onSave={handleSaveListItemConfig}
          onCreateVariable={handleCreateVariable}
        />
      )}
    </>
  )
}
